<div
  class="w-[450px] max-w-[90vw] rounded-2xl border border-gray-6 bg-white p-4 shadow-2xl backdrop-blur-md transition-all dark:bg-gray-2"
>
  <!-- Top header: Cover photo + Address & Owner + Market Status on right -->
  <div class="flex items-start justify-between gap-3 pb-3">
    <div class="flex items-center gap-3.5 min-w-0">
      <!-- Thumbnail Image -->
      <div
        class="relative h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-gray-6 bg-gray-3 shadow-xs"
      >
        @if (coverPhotoUrl(); as photoUrl) {
          <img [src]="photoUrl" alt="Photo du bien" class="h-full w-full object-cover" />
        } @else {
          <div class="flex h-full w-full flex-col items-center justify-center text-gray-9">
            <mat-icon class="text-2xl">home</mat-icon>
          </div>
        }
      </div>

      <!-- Title & Address info -->
      <div class="min-w-0 flex-1 flex flex-col justify-center gap-1">
        @if (property().location?.address?.street; as street) {
          <h4 class="m-0 truncate text-sm font-semibold text-gray-12" [title]="street">
            {{ street }}
          </h4>
        } @else if (property().presentation?.title; as title) {
          <h4 class="m-0 truncate text-sm font-semibold text-gray-12" [title]="title">
            {{ title }}
          </h4>
        } @else {
          <h4 class="m-0 truncate text-sm font-semibold text-gray-12">
            {{ property().definition?.propertyType | etxeEnumDisplay: 'propertyType' }}
          </h4>
        }

        <p class="m-0 truncate text-xs text-gray-11">
          {{ property().location?.address?.city || '' }}
          {{ property().location?.address?.zipCode ? property().location?.address?.zipCode : '' }}
        </p>

        <!-- Owner / Agent Badge -->
        @if (property().owner?.identity; as identity) {
          <div
            class="mt-0.5 inline-flex max-w-full items-center gap-1.5 self-start rounded-full border border-indigo-5 bg-indigo-3 px-2.5 py-0.5 text-[11px] font-medium text-indigo-11 truncate"
          >
            <mat-icon class="text-[13px]! h-3.5! w-3.5!">person</mat-icon>
            <span class="truncate"> {{ identity.firstName }} {{ identity.lastName }} </span>
          </div>
        }
      </div>
    </div>

    <!-- Market Status Badge (Right side of header) -->
    @if (property().marketStatus?.state; as marketState) {
      <div class="shrink-0">
        @switch (marketState) {
          @case ('for_sale') {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-3 text-indigo-11 border border-indigo-6"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-indigo-9"></span>
              À vendre
            </span>
          }
          @case ('under_mandate') {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-3 text-violet-11 border border-violet-6"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-violet-9"></span>
              Sous mandat
            </span>
          }
          @case ('under_offer') {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-3 text-amber-11 border border-amber-6"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-amber-9"></span>
              Offre acceptée
            </span>
          }
          @case ('under_contract') {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-3 text-orange-11 border border-orange-6"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-orange-9"></span>
              Sous compromis
            </span>
          }
          @case ('sold') {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-jade-3 text-jade-11 border border-jade-6"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-jade-9"></span>
              Vendu
            </span>
          }
          @case ('withdrawn') {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-3 text-gray-11 border border-gray-6"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-gray-8"></span>
              Retiré
            </span>
          }
          @default {
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-3 text-gray-11 border border-gray-6"
            >
              {{ marketState | etxeEnumDisplay: 'marketState' }}
            </span>
          }
        }
      </div>
    }
  </div>

  <!-- Divider -->
  <div class="border-t border-gray-5 my-2.5"></div>

  <!-- 2 Columns Spec Grid with dotted leaders -->
  <div class="grid grid-cols-2 gap-x-5 gap-y-1.5 text-xs">
    <!-- Left Column -->
    <div class="flex flex-col gap-1.5">
      <!-- Référence -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Référence</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span class="shrink-0 font-medium text-gray-12">{{ reference() }}</span>
      </div>

      <!-- Surface -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Surface</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span class="shrink-0 font-semibold text-gray-12">
          {{ property().keyInfo?.surface?.habitable || '-' }} m²
        </span>
      </div>

      <!-- Terrain (si disponible) -->
      @if (property().keyInfo?.surface?.land || property().keyInfo?.surface?.ground; as terrain) {
        <div class="flex items-baseline justify-between text-xs">
          <span class="shrink-0 text-gray-10">Terrain</span>
          <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
          <span class="shrink-0 font-medium text-gray-12">{{ terrain }} m²</span>
        </div>
      }

      <!-- Pièces -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Pièces</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span class="shrink-0 font-medium text-gray-12">
          {{ totalRooms() || '-' }}
        </span>
      </div>

      <!-- Chambres (si disponible) -->
      @if (bedroomCount(); as beds) {
        <div class="flex items-baseline justify-between text-xs">
          <span class="shrink-0 text-gray-10">Chambres</span>
          <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
          <span class="shrink-0 font-medium text-gray-12">{{ beds }}</span>
        </div>
      }

      <!-- Chauffage -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Chauffage</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span
          class="shrink-0 max-w-[110px] truncate font-medium text-gray-12 text-right"
          [title]="heatingLabel()"
        >
          {{ heatingLabel() }}
        </span>
      </div>

      <!-- Vue -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Vue</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span
          class="shrink-0 max-w-[110px] truncate font-medium text-gray-12 text-right"
          [title]="viewsLabel() || 'Non renseigné'"
        >
          {{ viewsLabel() || '-' }}
        </span>
      </div>
    </div>

    <!-- Right Column -->
    <div class="flex flex-col gap-1.5">
      <!-- Type de mandat & Commission -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Mandat</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span class="shrink-0 font-medium text-gray-12">
          {{ mandateTypeLabel() }}
          @if (targetCommissionRate(); as rate) {
            <span class="text-indigo-11 font-semibold">({{ rate }}%)</span>
          }
        </span>
      </div>

      <!-- Durée du mandat -->
      @if (mandateDurationLabel(); as dur) {
        <div class="flex items-baseline justify-between text-xs">
          <span class="shrink-0 text-gray-10">Durée mandat</span>
          <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
          <span class="shrink-0 font-medium text-gray-12">{{ dur }}</span>
        </div>
      }

      <!-- Prix de vente -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Prix de vente</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span class="shrink-0 font-semibold text-gray-12">
          @if (property().financials?.sellingPriceCents; as priceCents) {
            {{ priceCents / 100 | number: '1.0-0' : 'fr-FR' }} €
          } @else {
            -
          }
        </span>
      </div>

      <!-- Prix / m² -->
      @if (pricePerSquareMeter(); as ppm) {
        <div class="flex items-baseline justify-between text-xs">
          <span class="shrink-0 text-gray-10">Prix / m²</span>
          <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
          <span class="shrink-0 font-medium text-gray-12"> {{ ppm | number: '1.0-0' : 'fr-FR' }} €/m² </span>
        </div>
      }

      <!-- Disponibilité -->
      <div class="flex items-baseline justify-between text-xs">
        <span class="shrink-0 text-gray-10">Disponibilité</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <span class="shrink-0 font-medium text-gray-12">{{ availabilityLabel() }}</span>
      </div>

      <!-- Consommations (DPE) -->
      <div class="flex items-center justify-between text-xs">
        <span class="shrink-0 text-gray-10">Consommations</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <div class="shrink-0 flex items-center gap-1.5">
          <span class="font-medium text-gray-12">
            {{ property().dpe?.energyConsumption ? property().dpe?.energyConsumption : '-' }}
          </span>
          @if (property().dpe?.energyClass; as energy) {
            <span
              class="inline-flex h-4 w-4 items-center justify-center rounded text-[10px] font-bold text-white shadow-2xs"
              [style.background-color]="getDpeColor(energy)"
              [class.text-black!]="isDarkDpeText(energy)"
            >
              {{ energy }}
            </span>
          }
        </div>
      </div>

      <!-- Emissions GES -->
      <div class="flex items-center justify-between text-xs">
        <span class="shrink-0 text-gray-10">Emissions GES</span>
        <span class="mx-1 grow border-b border-dotted border-gray-6 mb-1"></span>
        <div class="shrink-0 flex items-center gap-1.5">
          <span class="font-medium text-gray-12">
            {{ property().dpe?.greenhouseGasEmission ? property().dpe?.greenhouseGasEmission : '-' }}
          </span>
          @if (property().dpe?.climateClass; as climate) {
            <span
              class="inline-flex h-4 w-4 items-center justify-center rounded text-[10px] font-bold text-white shadow-2xs"
              [style.background-color]="getDpeColor(climate)"
              [class.text-black!]="isDarkDpeText(climate)"
            >
              {{ climate }}
            </span>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Priorities Chips if any -->
  @if (mandatePriorities().length > 0) {
    <div class="border-t border-gray-5 mt-2.5 pt-2 flex flex-wrap items-center gap-1">
      <span class="text-[10px] text-gray-10 font-semibold uppercase tracking-wider mr-1">Attentes :</span>
      @for (priority of mandatePriorities(); track priority) {
        <span
          class="inline-flex items-center rounded-md bg-indigo-3 text-indigo-11 px-1.5 py-0.5 text-[10px] font-medium border border-indigo-5"
        >
          {{ priority }}
        </span>
      }
    </div>
  }
</div>
